import { makeAutoObservable, runInAction } from "mobx";

import { ApiError } from "@synthesis/shared-services";
import { setSnack } from "@synthesis/shared-ui";
import { logger } from "@synthesis/shared-utils";

import {
  SourceType,
  deleteFiles,
  deletePdfFiles,
  downloadDocumentDirect,
  downloadDocumentsZip,
  downloadPdfDirect,
  downloadPdfZip,
  postFile,
  postZipFile,
  renameFileRequest,
  type FileMetaDto,
} from "../../../api";
import { DocumentsTypes } from "../../../types";
import { fileMatchesTab } from "../../../utils/file-tab-filter";
import { DirectoryStore } from "../directory-store";
import { GeneralStore } from "../general-store";
import { type Content, StoreState, type StoreStateValue } from "../root-store";

const MAX_FILE_SIZE = 2147483648; // 2GB

type ProgressEvent = { loaded: number; total?: number };

type BlobRequestConfig = {
  onDownloadProgress?: (progressEvent: ProgressEvent) => void;
  onUploadProgress?: (progressEvent: ProgressEvent) => void;
};

const getErrorMessageCode = (err: unknown): string | undefined => {
  if (err instanceof ApiError) {
    return err.response?.errorCode;
  }

  if (typeof err === "object" && err !== null && "response" in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (data && typeof data === "object") {
      if ("messageCode" in data) return String((data as { messageCode?: string }).messageCode ?? "");
      if ("errorCode" in data) return String((data as { errorCode?: string }).errorCode ?? "");
    }
  }

  return undefined;
};

const toProgressPercent = (progressEvent: ProgressEvent): number => {
  if (!progressEvent.total) return 0;
  return Math.round((progressEvent.loaded / progressEvent.total) * 100);
};

export class FileStore {
  onlyModels: boolean;
  files: FileMetaDto[] = [];
  allFiles: FileMetaDto[] = [];
  state: StoreStateValue = StoreState.Initial;
  generalStore: GeneralStore;
  directoryStore: DirectoryStore;
  projectId: string | null = null;
  isFileUpload = false;
  progress: number | null = null;
  isDownloading = false;

  constructor(onlyModels: boolean, projectId: string, generalStore: GeneralStore, directoryStore: DirectoryStore) {
    this.onlyModels = onlyModels;
    this.projectId = projectId;
    this.generalStore = generalStore;
    this.directoryStore = directoryStore;
    makeAutoObservable(this);
  }

  private requireProjectId(): string {
    if (!this.projectId) {
      throw new Error("projectId is required");
    }
    return this.projectId;
  }

  get visibleFiles() {
    return this.files.filter((f) => fileMatchesTab(f, this.generalStore.activeTab));
  }

  async uploadFile(data: File) {
    if (data.size > MAX_FILE_SIZE) {
      setSnack({
        messageType: "error",
        title: `Максимальный суммарный размер файлов не должен превышать 2 ГБ`,
      });
      return;
    }
    const body = {
      sourceType: SourceType.PROJECT_ID,
      sourceId: this.requireProjectId(),
      parentId: this.directoryStore.rootFolderId,
      document: data,
    };

    this.state = StoreState.Loading;

    const timer = setTimeout(() => {
      this.isFileUpload = true;
    }, 3000);

    try {
      await postFile({
        body,
        options: {
          onUploadProgress: (progressEvent: ProgressEvent) => {
            runInAction(() => {
              this.progress = toProgressPercent(progressEvent);
            });
          },
        },
      });

      runInAction(() => {
        this.state = StoreState.Success;
        this.progress = null;
        this.isFileUpload = false;
        this.generalStore.getContents(this.directoryStore.rootFolderId);
        clearTimeout(timer);
      });
    } catch (err) {
      runInAction(() => {
        this.state = StoreState.Error;
        this.progress = null;
        this.isFileUpload = false;
        this.generalStore.getContents(this.directoryStore.rootFolderId);
        clearTimeout(timer);
      });
      logger.error("error upload file >>>", err);

      if (getErrorMessageCode(err) === "file.name.duplicate") {
        setSnack({
          title: "Файл с таким именем уже существует",
          messageType: "error",
        });
      }
    }
  }

  async renameFile(data: { name: string }, folderId: string) {
    const body = {
      id: folderId,
      newName: data.name,
    };

    this.state = StoreState.Loading;

    try {
      await renameFileRequest({ body });

      runInAction(() => {
        this.state = StoreState.Success;
        const patchName = (list: FileMetaDto[]) => list.map((f) => (f.id === folderId ? { ...f, baseName: data.name } : f));
        this.files = patchName(this.files);
        this.allFiles = patchName(this.allFiles);
        this.generalStore.getContents(this.directoryStore.rootFolderId);
      });
    } catch (err) {
      runInAction(() => {
        this.state = StoreState.Error;
      });

      if (getErrorMessageCode(err) === "file.name.duplicate") {
        setSnack({
          title: "Файл с таким именем уже существует",
          messageType: "error",
        });
      }

      logger.error("renameDerictory", err);
    }
  }

  async uploadZipFile(data: File) {
    if (data.size > MAX_FILE_SIZE) {
      setSnack({
        messageType: "error",
        title: `Не удалось загрузить файл ${data.name}. Zip-файл превышает допустимый размер 2Гб.`,
      });
      return;
    }

    const body = {
      sourceType: SourceType.PROJECT_ID,
      sourceId: this.requireProjectId(),
      parentId: this.directoryStore.rootFolderId,
      document: data,
    };

    this.state = StoreState.Loading;

    const timer = setTimeout(() => {
      this.isFileUpload = true;
    }, 3000);

    try {
      await postZipFile({
        body,
        options: {
          onUploadProgress: (progressEvent: ProgressEvent) => {
            runInAction(() => {
              this.progress = toProgressPercent(progressEvent);
            });
          },
        },
      });

      await new Promise((resolve) => setTimeout(resolve, 300));

      runInAction(() => {
        this.state = StoreState.Success;
        this.progress = null;
        this.isFileUpload = false;
        this.generalStore.getContents(this.directoryStore.rootFolderId);
        clearTimeout(timer);
      });
    } catch (err) {
      runInAction(() => {
        this.state = StoreState.Error;
        this.progress = null;
        this.isFileUpload = false;
        this.generalStore.getContents(this.directoryStore.rootFolderId);
        clearTimeout(timer);
      });
      logger.error("error upload file >>>", err);

      const messageCode = getErrorMessageCode(err);
      if (messageCode === "file.name.duplicate") {
        setSnack({
          title: "Файл с таким именем уже существует",
          messageType: "error",
        });
      }

      if (messageCode === "UPLOAD_MAX_FILE_SIZE_EXCEED") {
        setSnack({
          title: `Не удалось загрузить файл ${data.name}. Zip-файл содержит файлы превышающие 2Гб.`,
          messageType: "error",
        });
      }
    }
  }

  async uploadFiles(files: FileList) {
    const size = Array.from(files).reduce((acc, file) => acc + file.size, 0);
    if (size > MAX_FILE_SIZE) {
      setSnack({
        messageType: "error",
        title: `Максимальный суммарный размер файлов не должен превышать 2 ГБ`,
      });
      return;
    }

    for (const file of Array.from(files)) {
      const body = {
        sourceType: SourceType.PROJECT_ID,
        sourceId: this.requireProjectId(),
        parentId: this.directoryStore.rootFolderId,
        document: file,
      };

      this.state = StoreState.Loading;

      try {
        await postFile({
          body,
          options: {
            onUploadProgress: (progressEvent: ProgressEvent) => {
              runInAction(() => {
                this.progress = toProgressPercent(progressEvent);
              });
            },
          },
        });

        runInAction(() => {
          this.state = StoreState.Success;
          this.progress = null;
          this.generalStore.getContents(this.directoryStore.rootFolderId);
        });
      } catch (err) {
        runInAction(() => {
          this.state = StoreState.Error;
          this.progress = null;
          this.generalStore.getContents(this.directoryStore.rootFolderId);
        });

        if (getErrorMessageCode(err) === "file.name.duplicate") {
          setSnack({
            title: "Файл с таким именем уже существует",
            messageType: "error",
          });
        }

        logger.error("error upload files >>>", err);
      }
    }
  }

  async downloadArchiveFile(content?: Content) {
    this.state = StoreState.Loading;
    this.isDownloading = true;

    try {
      const config: BlobRequestConfig = {
        onDownloadProgress: (progressEvent) => {
          runInAction(() => {
            this.progress = toProgressPercent(progressEvent);
          });
        },
      };

      const checkedFilesIds = Array.from(this.generalStore.checkedFiles.keys());
      const ids = checkedFilesIds.length > 0 ? checkedFilesIds : content?.id ? [content.id] : [];

      const blobData = this.generalStore.activeTab === "docs" ? await downloadPdfZip(ids, config) : await downloadDocumentsZip(ids, config);
      const archiveName = content?.baseName ?? "archive";
      const file = new File([blobData], archiveName);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      const name = checkedFilesIds.length > 0 ? "archive.zip" : `${archiveName}.zip`;
      link.setAttribute("download", name);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      runInAction(() => {
        this.state = StoreState.Success;
      });
    } catch (err) {
      runInAction(() => {
        this.state = StoreState.Error;
      });
      logger.error("downloadFile", err);
    } finally {
      runInAction(() => {
        this.isDownloading = false;
      });
    }
  }

  async downloadFile(content: Content) {
    this.state = StoreState.Loading;
    this.isDownloading = true;

    try {
      const config: BlobRequestConfig = {
        onDownloadProgress: (progressEvent) => {
          runInAction(() => {
            this.progress = toProgressPercent(progressEvent);
          });
        },
      };
      const isPdf = content.ext?.toLowerCase() === "pdf";
      const blobData = isPdf ? await downloadPdfDirect(content.id, config) : await downloadDocumentDirect(content.id, config);
      const file = new File([blobData], content.baseName);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${content.baseName}.${content.ext}`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      runInAction(() => {
        this.state = StoreState.Success;
      });
    } catch (err) {
      runInAction(() => {
        this.state = StoreState.Error;
      });
      logger.error("downloadFile", err);
    } finally {
      runInAction(() => {
        this.isDownloading = false;
      });
    }
  }

  async deleteFile(id: string, userId: string) {
    this.state = StoreState.Loading;

    const file = this.files.find((item) => item.id === id);
    const isPdf = file?.type === DocumentsTypes.pdf_document || this.generalStore.activeTab === "docs";

    const body = {
      initUserId: userId,
      ids: [id],
    };

    try {
      await (isPdf ? deletePdfFiles : deleteFiles)({ body });

      runInAction(() => {
        if (isPdf) {
          const nextPdfPreviews = new Map(this.generalStore.pdfPreviewsByFileId);
          nextPdfPreviews.delete(id);
          this.generalStore.pdfPreviewsByFileId = nextPdfPreviews;
        }

        this.state = StoreState.Success;
        this.generalStore.getContents(this.directoryStore.rootFolderId);
        this.generalStore.checkedFiles = new Map();
      });
    } catch (err) {
      runInAction(() => {
        this.state = StoreState.Error;
      });
      logger.error("deleteFile", err);
    }
  }

  setSearchFiles(value: string | null) {
    runInAction(() => {
      if (!value) {
        this.files = this.allFiles;
        return;
      }

      const query = value.toLocaleLowerCase();
      this.files = this.allFiles.filter((file) => file.baseName?.toLocaleLowerCase().includes(query));
    });
  }
}
